import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Grid,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { CalendarToday, Visibility } from '@mui/icons-material';
import axios from 'axios';

interface News {
  id: string;
  title: string;
  content_th?: string;
  summary?: string;
  category: string;
  tags: string[];
  cover_image?: string;
  published_at: string;
  views: number;
  status: string;
}

const NewsSection = () => {
  const [news, setNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/v1/news`, {
        params: {
          limit: 6,
        },
      });
      setNews(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('ไม่สามารถโหลดข่าวสารได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      general: 'primary',
      event: 'secondary',
      announcement: 'success',
      achievement: 'info',
    };
    return colors[category.toLowerCase()] || 'default';
  };

  const getCategoryLabel = (category: string) => {
    const labels: { [key: string]: string } = {
      general: 'ทั่วไป',
      event: 'กิจกรรม',
      announcement: 'ประกาศ',
      achievement: 'ความสำเร็จ',
    };
    return labels[category.toLowerCase()] || category;
  };

  return (
    <Box id="news-section" sx={{ py: 8, backgroundColor: 'background.default' }}>
      <Container maxWidth="xl">
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
            textAlign: 'center',
          }}
        >
          ข่าวสารและกิจกรรม
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: 'text.secondary',
            mb: 6,
            textAlign: 'center',
          }}
        >
          ติดตามข่าวสารและกิจกรรมล่าสุดจากมหาวิทยาลัย
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        ) : news.length === 0 ? (
          <Alert severity="info" sx={{ mb: 4 }}>
            ยังไม่มีข่าวสารในขณะนี้
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {news.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <CardActionArea sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                    {item.cover_image && (
                      <CardMedia
                        component="img"
                        height="200"
                        image={item.cover_image}
                        alt={item.title}
                        sx={{ objectFit: 'cover' }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ mb: 2 }}>
                        <Chip
                          label={getCategoryLabel(item.category)}
                          color={getCategoryColor(item.category) as any}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          flexGrow: 1,
                        }}
                      >
                        {item.summary || item.content_th?.substring(0, 150) + '...'}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                          <CalendarToday sx={{ fontSize: 16 }} />
                          <Typography variant="caption">
                            {formatDate(item.published_at)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                          <Visibility sx={{ fontSize: 16 }} />
                          <Typography variant="caption">{item.views || 0}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default NewsSection;
